import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { format } from "date-fns";
import { tr } from "date-fns/locale";
import { getDateLocale } from "@/utils/dateUtils";
import { 
  Calendar, Wallet, TrendingUp, Shield, Lock, Banknote,
  LayoutDashboard, Users, Layers, MessageCircle, Check, CheckCircle, ShieldCheck,
  FileText, Edit, Crown
} from "lucide-react";
import { API } from "@/App";
import { DatePickerTR } from "@/components/ui/date-picker-tr";
import { formatDateToLocal } from "@/lib/utils";
import { formatTurkishCurrency, parseTurkishNumber } from "@/utils/numberUtils";
import { useLocalizedData } from "@/contexts/LocalizedDataContext";
import { useCurrency } from "@/contexts/CurrencyContext";
import { useTranslation } from "react-i18next";

export default function EmployeeEditModal({
  isOpen,
  onClose,
  employee,
  businessId,
  businessAcceptedCards = [],
  businessTrainingTypes = [],
  onSuccess,
}) {
  const { t } = useTranslation(["common", "dashboard", "members", "trainers", "reservations", "settings", "payments", "rooms", "messaging", "business", "memberDashboard", "packages", "admin", "employees", "surveys", "reports", "evaluations"]);
  const { formatPrice, currencySymbol } = useCurrency();
  const { getLocalizedBranch, getLocalizedTrainingType } = useLocalizedData();

  const [editingEmployee, setEditingEmployee] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [newSalaryAmount, setNewSalaryAmount] = useState("");
  const [salaryEffectiveDate, setSalaryEffectiveDate] = useState("");
  const [businessCardPrices, setBusinessCardPrices] = useState({});
  const [previousPermissions, setPreviousPermissions] = useState(null); // Eski izinleri saklamak için

  useEffect(() => {
    if (employee && isOpen) {
      setPreviousPermissions(null);
      setEditingEmployee({
        ...employee,
        name: employee.name || "",
        surname: employee.surname || "",
        position: employee.position || employee.role || "",
        work_type: employee.work_type || "salary_receiving",
        current_salary: employee.current_salary || 0,
        start_date: employee.start_date || "",
        salary_start_date: employee.salary_start_date || "",
        commission_settings: employee.commission_settings || {
          calculation_method: "per_lesson",
          card_commissions: {},
          package_commissions: { type: "percentage", rates: {} }
        },
        is_manager: employee.is_manager || false,
        is_co_owner: employee.is_co_owner || false,
        permissions: employee.permissions || {},
        can_approve_reservations: employee.can_approve_reservations || false,
      });
      setNewSalaryAmount("");
      setSalaryEffectiveDate("");
      fetchBusinessCardPrices();
    }
  }, [employee, isOpen]);

  const fetchBusinessCardPrices = async () => {
    try {
      const res = await fetch(`${API}/businesses/${businessId}/card-prices`, { credentials: "include" });
      if (res.ok) {
        const data = await res.json();
        const cardPricesArray = data.card_prices || [];
        const cardPricesObj = {};
        if (Array.isArray(cardPricesArray)) {
          cardPricesArray.forEach(cp => {
            if (cp.card_name) {
              cardPricesObj[cp.card_name] = cp.single_session_price || cp.price || 0;
            }
          });
        }
        setBusinessCardPrices(cardPricesObj);
      }
    } catch (error) {
      console.error("Error fetching card prices:", error);
    }
  };

  const handleSave = async () => {
    if (!editingEmployee) return;
    if (isSaving) return;
    setIsSaving(true);

    try {
      const cleanCommissionSettings = {
        calculation_method: editingEmployee.commission_settings?.calculation_method || "per_lesson",
        card_commissions: {},
        package_commissions: {
          type: editingEmployee.commission_settings?.package_commissions?.type || "percentage",
          rates: {}
        }
      };

      if (editingEmployee.commission_settings?.card_commissions) {
        Object.entries(editingEmployee.commission_settings.card_commissions).forEach(([cardName, cardConfig]) => {
          const cleanRates = {};
          if (cardConfig.rates) {
            Object.entries(cardConfig.rates).forEach(([trainingType, rate]) => {
              cleanRates[trainingType] = rate === "" || rate === null || rate === undefined ? 0 : parseFloat(rate) || 0;
            });
          }
          cleanCommissionSettings.card_commissions[cardName] = {
            type: cardConfig.type || "percentage",
            rates: cleanRates
          };
        });
      }

      if (editingEmployee.commission_settings?.package_commissions?.rates) {
        Object.entries(editingEmployee.commission_settings.package_commissions.rates).forEach(([trainingType, rate]) => {
          cleanCommissionSettings.package_commissions.rates[trainingType] = 
            rate === "" || rate === null || rate === undefined ? 0 : parseFloat(rate) || 0;
        });
      }

      const updateData = {
        position: editingEmployee.position,
        work_type: editingEmployee.work_type,
        current_salary: parseFloat(editingEmployee.current_salary) || 0,
        start_date: editingEmployee.start_date,
        is_manager: editingEmployee.is_manager || false,
        is_co_owner: editingEmployee.is_co_owner || false,
        permissions: editingEmployee.permissions || {},
        commission_settings: editingEmployee.work_type !== "salary_receiving" ? cleanCommissionSettings : null,
        can_approve_reservations: editingEmployee.can_approve_reservations || false
      };

      if (newSalaryAmount && salaryEffectiveDate) {
        updateData.new_salary = parseTurkishNumber(newSalaryAmount);
        updateData.salary_effective_date = salaryEffectiveDate;
      }

      const response = await fetch(`${API}/businesses/${businessId}/employees/${employee.employee_id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(updateData),
      });

      if (response.ok) {
        toast.success(t('employees:employeeUpdated'));
        onClose();
        onSuccess?.();
      } else {
        const error = await response.json();
        let errorMessage = t('settings:updateFailed');
        if (error.detail) {
          if (typeof error.detail === "string") {
            errorMessage = error.detail;
          } else if (Array.isArray(error.detail)) {
            errorMessage = error.detail.map(e => e.msg || e.message || JSON.stringify(e)).join(", ");
          }
        }
        toast.error(errorMessage);
      }
    } catch (error) {
      toast.error(t('common:errorOccurred'));
    } finally {
      setIsSaving(false);
    }
  };

  if (!editingEmployee) return null;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-xl rounded-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{t('employees:editEmployee')}</DialogTitle>
        </DialogHeader>

        <Tabs defaultValue="info" className="w-full">
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="info">{t('common:information')}</TabsTrigger>
            <TabsTrigger value="commission">{t('common:commission')}</TabsTrigger>
            <TabsTrigger value="permissions">{t("common:i̇zinler")}</TabsTrigger>
          </TabsList>

          <TabsContent value="info" className="space-y-4 mt-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>{t('common:firstName')}</Label>
                <Input value={editingEmployee.name || ""} disabled className="rounded-xl mt-1 bg-slate-50" />
              </div>
              <div>
                <Label>{t('common:lastName')}</Label>
                <Input value={editingEmployee.surname || ""} disabled className="rounded-xl mt-1 bg-slate-50" />
              </div>
            </div>

            <div>
              <Label>{t('common:position')}</Label>
              <Input value={editingEmployee.position || ""} onChange={(e) => setEditingEmployee({...editingEmployee, position: e.target.value})} className="rounded-xl mt-1" />
            </div>

            <div className="p-4 bg-green-50 rounded-xl border border-green-200">
              <h4 className="font-semibold text-green-700 flex items-center gap-2 mb-3">
                <Calendar className="w-4 h-4" /> {t('common:startDateLabel')}
              </h4>
              <div className="flex items-center justify-between">
                <span className="text-sm text-grey">{t('common:current')}:</span>
                <span className="font-medium text-green-700">
                  {editingEmployee.start_date 
                    ? format(new Date(editingEmployee.start_date), "dd/MM/yyyy", { locale: getDateLocale() })
                    : t("common:notSpecified")}
                </span>
              </div>
              <div className="mt-3">
                <Label className="text-sm">{t('common:enterNewDate')}</Label>
                <DatePickerTR
                  value={editingEmployee.start_date ? new Date(editingEmployee.start_date) : null}
                  onChange={(date) => setEditingEmployee({...editingEmployee, start_date: date ? formatDateToLocal(date) : ""})}
                  placeholder={t('common:selectDate')}
                  className="mt-1"
                />
              </div>
            </div>

            <div>
              <Label>{t("common:workingType")}</Label>
              <Select value={editingEmployee.work_type || "salary_receiving"} onValueChange={(v) => setEditingEmployee({...editingEmployee, work_type: v})}>
                <SelectTrigger className="rounded-xl mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="salary_receiving" className="text-green-700">
                    <div className="flex items-center gap-2">
                      <span className="text-green-600">💵</span> {t('employees:salaryReceiver')}
                    </div>
                  </SelectItem>
                  <SelectItem value="salary_and_commission" className="text-green-700">
                    <div className="flex items-center gap-2">
                      <span className="text-green-600">💎</span> {t('employees:salaryAndCommissionReceiver')}
                    </div>
                  </SelectItem>
                  <SelectItem value="commission_only" className="text-green-700">
                    <div className="flex items-center gap-2">
                      <span className="text-green-600">💰</span> {t('employees:commissionOnly')}
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>
              
              {/* Work Type Explanation */}
              <div className="p-3 rounded-xl text-sm mt-2 bg-green-50 text-green-800">
                {editingEmployee.work_type === 'salary_receiving' && (
                  <p>{t('employees:salaryReceiverDesc')}</p>
                )}
                {editingEmployee.work_type === 'salary_and_commission' && (
                  <p>{t('employees:salaryAndCommissionDesc')}</p>
                )}
                {editingEmployee.work_type === 'commission_only' && (
                  <p>{t('employees:commissionOnlyDesc')}</p>
                )}
              </div>
            </div>

            {editingEmployee.work_type !== "commission_only" && (
              <div className="p-4 bg-blue-50 rounded-xl space-y-3 border border-blue-200">
                <h4 className="font-semibold text-blue-700 flex items-center gap-2">
                  <Wallet className="w-4 h-4" /> {t('common:salarySettings')}
                </h4>
                <div className="flex items-center justify-between p-2 bg-white rounded-lg">
                  <span className="text-sm text-grey">{t('common:currentSalary')}:</span>
                  <span className="font-bold text-blue-600">{formatTurkishCurrency((editingEmployee.current_salary || 0))}</span>
                </div>
                {editingEmployee.salary_start_date && (
                  <div className="flex items-center justify-between p-2 bg-white rounded-lg">
                    <span className="text-sm text-grey">{t('common:salaryStart')}:</span>
                    <span className="font-medium text-blue-600">
                      {format(new Date(editingEmployee.salary_start_date), "dd/MM/yyyy", { locale: getDateLocale() })}
                    </span>
                  </div>
                )}
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-sm">{t('common:newSalary')} ({currencySymbol})</Label>
                    <Input 
                      type="number" 
                      value={newSalaryAmount} 
                      onChange={(e) => setNewSalaryAmount(e.target.value)} 
                      placeholder={t("employees:newSalaryAmount")} 
                      className="rounded-xl mt-1" 
                    />
                  </div>
                  <div>
                    <Label className="text-sm">{t("common:validityDate")}</Label>
                    <DatePickerTR 
                      value={salaryEffectiveDate ? new Date(salaryEffectiveDate) : null} 
                      onChange={(date) => setSalaryEffectiveDate(date ? formatDateToLocal(date) : "")} 
                      placeholder={t('common:selectDate')} 
                      className="mt-1" 
                    />
                  </div>
                </div>
                <p className="text-xs text-blue-600">{t("employees:yeniMaasGirildiktenSonraHakedisler")}</p>
              </div>
            )}

            <div className="flex items-center justify-between p-3 bg-amber-50 rounded-xl">
              <div className="flex items-center gap-2">
                <ShieldCheck className="w-5 h-5 text-amber-600" />
                <div>
                  <p className="font-medium text-amber-800">{t('employees:manager')}</p>
                  <p className="text-xs text-amber-600">{t("common:fullMenuAccess")}</p>
                </div>
              </div>
              <Switch
                checked={editingEmployee.is_co_owner || false}
                onCheckedChange={(c) => {
                  if (c) {
                    // 1. Kapatılırsa eski haline dönebilmek için mevcut durumu yedekle
                    setPreviousPermissions({
                      permissions: { ...editingEmployee.permissions },
                      is_manager: editingEmployee.is_manager,
                      can_approve_reservations: editingEmployee.can_approve_reservations
                    });

                    // 2. Alttaki tüm izinleri true yap
                    const allPermissionsTrue = {
                      // Genel
                      dashboard: true,
                      dashboard_overview: true,
                      branches: true,
                      analytics: true,
                      subscription: true,
                      // Üye Yönetimi
                      members: true,
                      members_list: true,
                      all_packages: true,
                      member_forms: true,
                      qr_scan: true,
                      measurements: true,
                      can_deactivate_members: true,
                      // Rezervasyonlar
                      reservations: true,
                      manual_reservation_complete: true,
                      manual_reservation_approve: true,
                      backdate_reservations: true,
                      lesson_note_edit: true,
                      // Ödemeler
                      finances: true,
                      payments: true,
                      salary_payments: true,
                      bonus_payments: true,
                      business_expenses: true,
                      trainer_collections: true,
                      commissions: true,
                      reports: true,
                      // İşletme Ayarları
                      settings: true,
                      rooms: true,
                      packages: true,
                      price_list: true,
                      trainers: true,
                      employees: true,
                      settings_business_info: true,
                      settings_pricing: true,
                      settings_accruals: true,
                      settings_working_hours: true,
                      settings_branches: true,
                      settings_training_types: true,
                      settings_measurements: true,
                      settings_expense_types: true,
                      settings_numbering: true,
                      settings_currency: true,
                      member_no_edit: true,
                      package_no_edit: true,
                      // İletişim
                      communication: true,
                      messages: true,
                      ratings: true,
                      surveys: true,
                      events: true,
                      evaluations: true,
                      goals: true,
                      // Özel
                      can_view_revenue: true,
                      transfer_package_sale: true
                    };

                    setEditingEmployee(prev => ({
                      ...prev,
                      is_co_owner: true,
                      is_manager: true,
                      can_approve_reservations: true,
                      permissions: allPermissionsTrue
                    }));
                  } else {
                    // Şalter kapatıldı -> Eğer yedeğimiz varsa eski haline döndür
                    if (previousPermissions) {
                      setEditingEmployee(prev => ({
                        ...prev,
                        is_co_owner: false,
                        is_manager: previousPermissions.is_manager,
                        can_approve_reservations: previousPermissions.can_approve_reservations,
                        permissions: previousPermissions.permissions
                      }));
                      setPreviousPermissions(null);
                    } else {
                      setEditingEmployee(prev => ({
                        ...prev,
                        is_co_owner: false
                      }));
                    }
                  }
                }}
              />
            </div>
          </TabsContent>

          <TabsContent value="commission" className="space-y-4 mt-4">
            {editingEmployee.work_type === "salary_receiving" ? (
              <div className="text-center py-8 text-grey">
                <Banknote className="w-12 h-12 mx-auto mb-2 opacity-30" />
                <p>{t('common:noCommissionSetup')}</p>
              </div>
            ) : (
              <>
                <div className="p-3 bg-blue-50 rounded-xl space-y-2">
                  <Label className="font-medium text-blue-800">{t('trainers:commissionCalculation')}</Label>
                  <div className="flex flex-col gap-2">
                    <label className="flex items-start gap-3 p-2 bg-white rounded-lg cursor-pointer border text-sm">
                      <input type="radio" checked={editingEmployee.commission_settings?.calculation_method !== "per_package_sale"} onChange={() => setEditingEmployee({...editingEmployee, commission_settings: {...editingEmployee.commission_settings, calculation_method: "per_lesson"}})} className="mt-0.5" />
                      <div className="flex items-center gap-2">
                        <TrendingUp className="w-4 h-4 text-green-600" />
                        <p className="font-medium">{t('trainers:perLesson')}</p>
                      </div>
                    </label>
                    <label className="flex items-start gap-3 p-2 bg-white rounded-lg cursor-pointer border text-sm">
                      <input type="radio" checked={editingEmployee.commission_settings?.calculation_method === "per_package_sale"} onChange={() => setEditingEmployee({...editingEmployee, commission_settings: {...editingEmployee.commission_settings, calculation_method: "per_package_sale"}})} className="mt-0.5" />
                      <div className="flex items-center gap-2">
                        <Banknote className="w-4 h-4 text-blue-600" />
                        <p className="font-medium">{t("packages:byPackageSale")}</p>
                      </div>
                    </label>
                  </div>
                </div>

                {businessAcceptedCards.length > 0 && (
                  <div className="space-y-2">
                    <Label className="font-medium text-amber-800 text-sm flex items-center gap-2">
                      <span className="w-2 h-2 bg-amber-500 rounded-full"></span>
                      {t('reports:cardCommissions')}
                    </Label>
                    {businessAcceptedCards.map(cardName => {
                      const cardConfig = editingEmployee.commission_settings?.card_commissions?.[cardName] || { type: "percentage", rates: {} };
                      const cardPrice = businessCardPrices[cardName] || 0;
                      return (
                        <div key={cardName} className="p-3 bg-amber-50 rounded-xl space-y-2">
                          <div className="flex items-center justify-between">
                            <span className="font-medium text-amber-700 text-sm">{cardName}</span>
                            <Select value={cardConfig.type || "percentage"} onValueChange={(v) => setEditingEmployee({...editingEmployee, commission_settings: {...editingEmployee.commission_settings, card_commissions: {...editingEmployee.commission_settings?.card_commissions, [cardName]: { ...cardConfig, type: v }}}})}>
                              <SelectTrigger className="w-28 h-8 rounded-lg text-sm"><SelectValue /></SelectTrigger>
                              <SelectContent>
                            <SelectItem value="percentage">{t('reports:percentageLabel')}</SelectItem>
                                <SelectItem value="fixed">{t('common:fixed')} ({currencySymbol})</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                          {cardConfig.type === "fixed" && cardPrice <= 0 && (
                            <p className="text-xs text-red-500 bg-red-50 p-2 rounded">
                              {t('common:priceNotSetForCard')}
                            </p>
                          )}
                          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                            {businessTrainingTypes.map(trainingType => {
                              const maxFixedValue = cardConfig.type === "fixed" ? cardPrice : undefined;
                              const currentValue = cardConfig.rates?.[trainingType] || "";
                              const isOverMax = cardConfig.type === "fixed" && cardPrice > 0 && parseFloat(currentValue) > cardPrice;
                              
                              return (
                                <div key={trainingType}>
                                  <Label className="text-xs text-amber-600">{getLocalizedTrainingType ? getLocalizedTrainingType(trainingType) : trainingType}</Label>
                                  <Input
                                    type="number"
                                    min="0"
                                    max={cardConfig.type === "percentage" ? 100 : maxFixedValue}
                                    step="1"
                                    value={currentValue}
                                    onChange={(e) => {
                                      let value = e.target.value === "" ? "" : parseFloat(e.target.value) || 0;
                                      if (cardConfig.type === "percentage" && value > 100) value = 100;
                                      if (cardConfig.type === "fixed" && cardPrice > 0 && value > cardPrice) {
                                        value = cardPrice;
                        toast.error(`Max ${currencySymbol}${cardPrice}`);
                                      }
                                      setEditingEmployee({...editingEmployee, commission_settings: {...editingEmployee.commission_settings, card_commissions: {...editingEmployee.commission_settings?.card_commissions, [cardName]: {...cardConfig, rates: {...cardConfig.rates, [trainingType]: value}}}}});
                                    }}
                                    className={`rounded-lg h-8 text-sm ${isOverMax ? 'border-red-500' : ''}`}
                                    placeholder={cardConfig.type === "fixed" ? (cardPrice > 0 ? `Max ${currencySymbol}${cardPrice}` : currencySymbol) : "% (0-100)"}
                                    disabled={cardConfig.type === "fixed" && cardPrice <= 0}
                                  />
                                  {isOverMax && <p className="text-xs text-red-500 mt-0.5">Max: {currencySymbol}{cardPrice}</p>}
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}

                <div className="space-y-2">
                  <Label className="font-medium text-emerald-800 text-sm flex items-center gap-2">
                    <span className="w-2 h-2 bg-emerald-500 rounded-full"></span>
                    {t('packages:packageCommissions')}
                  </Label>
                  <div className="p-3 bg-emerald-50 rounded-xl space-y-2">
                    <Select value={editingEmployee.commission_settings?.package_commissions?.type || "percentage"} onValueChange={(v) => setEditingEmployee({...editingEmployee, commission_settings: {...editingEmployee.commission_settings, package_commissions: {...editingEmployee.commission_settings?.package_commissions, type: v}}})}>
                      <SelectTrigger className="w-full h-8 rounded-lg text-sm"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="percentage">{t('reports:percentageLabel')}</SelectItem>
                        <SelectItem value="fixed">{t('common:fixed')} ({currencySymbol})</SelectItem>
                      </SelectContent>
                    </Select>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                      {businessTrainingTypes.map(type => (
                        <div key={type}>
                          <Label className="text-xs text-emerald-600">{getLocalizedTrainingType ? getLocalizedTrainingType(type) : type}</Label>
                          <Input
                            type="number"
                            min="0"
                            step="1"
                            value={editingEmployee.commission_settings?.package_commissions?.rates?.[type] || ""}
                            onChange={(e) => {
                              const value = e.target.value === "" ? "" : parseFloat(e.target.value) || 0;
                              setEditingEmployee({...editingEmployee, commission_settings: {...editingEmployee.commission_settings, package_commissions: {...editingEmployee.commission_settings?.package_commissions, rates: {...editingEmployee.commission_settings?.package_commissions?.rates, [type]: value}}}});
                            }}
                            className="rounded-lg h-8 text-sm"
                            placeholder={editingEmployee.commission_settings?.package_commissions?.type === "fixed" ? currencySymbol : "%"}
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </>
            )}
          </TabsContent>

          <TabsContent value="permissions" className="space-y-4 mt-4">
            {/* Şirket Ortağı (Kurucu Ortak) Şalteri */}
            <div className={`p-4 rounded-xl border-2 mb-3 ${editingEmployee.is_co_owner ? 'bg-emerald-50 border-emerald-300' : 'bg-slate-50 border-slate-200'}`}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center ${editingEmployee.is_co_owner ? 'bg-emerald-600' : 'bg-slate-300'}`}>
                    <Crown className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <span className="font-semibold text-navy flex items-center gap-2">
                      Şirket Ortağı (Tam Yetki)
                    </span>
                    <p className="text-xs text-grey">
                      {editingEmployee.is_co_owner 
                        ? "Ana işletme sahibi gibi tüm finans, kasa, prim, çalışan ve yönetim ekranlarına tam erişim sağlar." 
                        : "Şirket ortağı olarak atanmamış."}
                    </p>
                  </div>
                </div>
                <Switch
                  checked={editingEmployee.is_co_owner || false}
                  onCheckedChange={(c) => {
                    setEditingEmployee(prev => ({
                      ...prev,
                      is_co_owner: c,
                      is_manager: c ? true : prev.is_manager, // Ortak ise yönetici de otomatik açık olsun
                    }));
                  }}
                />
              </div>
            </div>

            <div className={`p-4 rounded-xl border-2 ${editingEmployee.is_manager ? 'bg-green-50 border-green-300' : 'bg-slate-50 border-slate-200'}`}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center ${editingEmployee.is_manager ? 'bg-green-500' : 'bg-slate-300'}`}>
                    <Shield className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <span className="font-semibold text-navy">{t('employees:manager')}</span>
                    <p className="text-xs text-grey">
                      {editingEmployee.is_manager 
                        ? t("employees:isManagerDesc") 
                        : t("employees:notManagerDesc")}
                    </p>
                  </div>
                </div>
                <Switch
                  checked={editingEmployee.is_manager || false}
                  onCheckedChange={(c) => {
                    if (!c) {
                      setEditingEmployee({
                        ...editingEmployee, 
                        is_manager: false,
                        permissions: {
                          ...editingEmployee.permissions,
                          branches: false, analytics: false, subscription: false,
                          salary_payments: false, bonus_payments: false,
                          business_expenses: false, trainer_collections: false,
                          commissions: false, reports: false, rooms: false,
                          packages: false, price_list: false, trainers: false,
                          employees: false, settings: false,
                        }
                      });
                    } else {
                      setEditingEmployee({...editingEmployee, is_manager: true});
                    }
                  }}
                />
              </div>
            </div>

            {!editingEmployee.is_manager && (
              <div className="p-3 bg-amber-50 border border-amber-200 rounded-xl">
                <p className="text-xs text-amber-800 flex items-center gap-2">
                  <Lock className="w-3 h-3" />
                  <span><strong>{t('common:needToMakeManagerFirst')}</strong></span>
                </p>
              </div>
            )}

            <div className="p-3 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl border border-blue-100">
              <Label className="text-sm font-medium text-navy mb-2 block">{t("employees:permissionTemplates")}</Label>
              <div className="flex flex-wrap gap-2">
                <Button size="sm" variant="outline" className="text-xs rounded-lg bg-white hover:bg-green-50 hover:border-green-300" disabled={!editingEmployee.is_manager}
                  onClick={() => {
                    const allPerms = {
                      // Genel
                      dashboard: true, dashboard_overview: true, branches: true, analytics: true, subscription: true,
                      // Üye Yönetimi
                      members: true, members_list: true, all_packages: true, member_forms: true, qr_scan: true, measurements: true,
                      // Rezervasyonlar
                      reservations: true,
                      // Ödemeler
                      finances: true, payments: true, salary_payments: true, bonus_payments: true,
                      business_expenses: true, trainer_collections: true, commissions: true, reports: true,
                      // İşletme Ayarları
                      settings: true, rooms: true, packages: true, price_list: true, trainers: true, employees: true,
                      // İletişim
                      communication: true, messages: true, ratings: true, surveys: true, events: true, evaluations: true, goals: true,
                      // Özel
                      can_view_revenue: true
                    };
                    setEditingEmployee({...editingEmployee, permissions: allPerms});
                  }}>
                  {t('common:fullAuthority')} {!editingEmployee.is_manager && `(${t('common:managerRequired')})`}
                </Button>
                <Button size="sm" variant="outline" className="text-xs rounded-lg bg-white hover:bg-blue-50 hover:border-blue-300"
                  onClick={() => {
                    const dersPerms = {
                      // Genel
                      dashboard: true, dashboard_overview: true, branches: false, analytics: false, subscription: false,
                      // Üye Yönetimi
                      members: true, members_list: true, all_packages: true, member_forms: false, qr_scan: true, measurements: true,
                      // Rezervasyonlar
                      reservations: true,
                      // Ödemeler
                      finances: false, payments: false, salary_payments: false, bonus_payments: false,
                      business_expenses: false, trainer_collections: false, commissions: false, reports: false,
                      // İşletme Ayarları
                      settings: false, rooms: false, packages: false, price_list: false, trainers: false, employees: false,
                      // İletişim
                      communication: true, messages: true, ratings: false, surveys: false, events: false, evaluations: false, goals: false,
                      // Özel
                      can_view_revenue: false
                    };
                    setEditingEmployee({...editingEmployee, permissions: dersPerms});
                  }}>
                  {t('common:onlyLessons')}
                </Button>
                <Button size="sm" variant="outline" className="text-xs rounded-lg bg-white hover:bg-red-50 hover:border-red-300"
                  onClick={() => {
                    const noPerms = {
                      // Genel
                      dashboard: false, dashboard_overview: false, branches: false, analytics: false, subscription: false,
                      // Üye Yönetimi
                      members: false, members_list: false, all_packages: false, member_forms: false, qr_scan: false, measurements: false,
                      // Rezervasyonlar
                      reservations: false,
                      // Ödemeler
                      finances: false, payments: false, salary_payments: false, bonus_payments: false,
                      business_expenses: false, trainer_collections: false, commissions: false, reports: false,
                      // İşletme Ayarları
                      settings: false, rooms: false, packages: false, price_list: false, trainers: false, employees: false,
                      // İletişim
                      communication: false, messages: false, ratings: false, surveys: false, events: false, evaluations: false, goals: false,
                      // Özel
                      can_view_revenue: false
                    };
                    setEditingEmployee({...editingEmployee, permissions: noPerms});
                  }}>
                  {t('common:closeAll')}
                </Button>
              </div>
            </div>

            <div className="border rounded-xl p-3 space-y-2">
              <div className="flex items-center gap-2 mb-2"><LayoutDashboard className="w-4 h-4 text-navy" /><span className="font-medium text-navy">{t('common:general')}</span></div>
              <div className="space-y-1.5">
                <div className="flex items-center justify-between p-2 bg-green-50 rounded-lg text-sm"><span className="text-green-700">Harita</span><span className="text-xs text-green-600 bg-green-100 px-2 py-0.5 rounded">{t("common:public")}</span></div>
                <div className="flex items-center justify-between p-2 bg-slate-50 rounded-lg text-sm"><span className="text-grey">{t("common:overview")}</span><Switch checked={editingEmployee.permissions?.dashboard_overview !== false} onCheckedChange={(c) => setEditingEmployee({...editingEmployee, permissions: {...editingEmployee.permissions, dashboard_overview: c}})} /></div>
                <div className={`flex items-center justify-between p-2 bg-slate-50 rounded-lg text-sm ${!editingEmployee.is_manager ? 'opacity-50' : ''}`}><span className="text-grey flex items-center gap-1">{t('common:branches')} <span className="text-xs text-amber-600 bg-amber-100 px-1 rounded">{t('employees:manager')}</span></span><Switch checked={editingEmployee.permissions?.branches || false} disabled={!editingEmployee.is_manager} onCheckedChange={(c) => setEditingEmployee({...editingEmployee, permissions: {...editingEmployee.permissions, branches: c}})} /></div>
                <div className={`flex items-center justify-between p-2 bg-slate-50 rounded-lg text-sm ${!editingEmployee.is_manager ? 'opacity-50' : ''}`}><span className="text-grey flex items-center gap-1">{t('dashboard:analytics.analytics')} <span className="text-xs text-amber-600 bg-amber-100 px-1 rounded">{t('employees:manager')}</span></span><Switch checked={editingEmployee.permissions?.analytics || false} disabled={!editingEmployee.is_manager} onCheckedChange={(c) => setEditingEmployee({...editingEmployee, permissions: {...editingEmployee.permissions, analytics: c}})} /></div>
                <div className={`flex items-center justify-between p-2 bg-slate-50 rounded-lg text-sm ${!editingEmployee.is_manager ? 'opacity-50' : ''}`}><span className="text-grey flex items-center gap-1">{t('common:subscription')} <span className="text-xs text-amber-600 bg-amber-100 px-1 rounded">{t('employees:manager')}</span></span><Switch checked={editingEmployee.permissions?.subscription || false} disabled={!editingEmployee.is_manager} onCheckedChange={(c) => setEditingEmployee({...editingEmployee, permissions: {...editingEmployee.permissions, subscription: c}})} /></div>
              </div>
            </div>

            <div className="border rounded-xl p-3 space-y-2">
              <div className="flex items-center gap-2 mb-2"><Users className="w-4 h-4 text-navy" /><span className="font-medium text-navy">{t("common:memberManagement")}</span></div>
              <div className="space-y-1.5">
                <div className="flex items-center justify-between p-2 bg-slate-50 rounded-lg text-sm"><span className="text-grey">{t('members:title')}</span><Switch checked={editingEmployee.permissions?.members_list !== false} onCheckedChange={(c) => setEditingEmployee({...editingEmployee, permissions: {...editingEmployee.permissions, members_list: c}})} /></div>
                <div className="flex items-center justify-between p-2 bg-slate-50 rounded-lg text-sm"><span className="text-grey">{t('members:allPackages')}</span><Switch checked={editingEmployee.permissions?.all_packages || false} onCheckedChange={(c) => setEditingEmployee({...editingEmployee, permissions: {...editingEmployee.permissions, all_packages: c}})} /></div>
                <div className="flex items-center justify-between p-2 bg-slate-50 rounded-lg text-sm"><span className="text-grey">{t("surveys:memberForms")}</span><Switch checked={editingEmployee.permissions?.member_forms || false} onCheckedChange={(c) => setEditingEmployee({...editingEmployee, permissions: {...editingEmployee.permissions, member_forms: c}})} /></div>
                <div className="flex items-center justify-between p-2 bg-slate-50 rounded-lg text-sm"><span className="text-grey">{t('common:qrScan')}</span><Switch checked={editingEmployee.permissions?.qr_scan || false} onCheckedChange={(c) => setEditingEmployee({...editingEmployee, permissions: {...editingEmployee.permissions, qr_scan: c}})} /></div>
                <div className="flex items-center justify-between p-2 bg-slate-50 rounded-lg text-sm"><span className="text-grey">{t("evaluations:measurements")}</span><Switch checked={editingEmployee.permissions?.measurements || false} onCheckedChange={(c) => setEditingEmployee({...editingEmployee, permissions: {...editingEmployee.permissions, measurements: c}})} /></div>
              </div>
            </div>

            <div className="border rounded-xl p-3 space-y-2">
              <div className="flex items-center justify-between"><div className="flex items-center gap-2"><Calendar className="w-4 h-4 text-navy" /><span className="font-medium text-navy">{t('common:reservations')}</span></div><Switch checked={editingEmployee.permissions?.reservations !== false} onCheckedChange={(c) => setEditingEmployee({...editingEmployee, permissions: {...editingEmployee.permissions, reservations: c}})} /></div>
              <div className="flex items-center justify-between p-2 bg-green-50 rounded-lg text-sm"><span className="text-grey flex items-center gap-1"><Check className="w-3 h-3" /> {t('common:directApprovalAuthority')} <span className="text-xs text-green-600 bg-green-100 px-1 rounded">{t("admin:uyeOnayiniAtla")}</span></span><Switch checked={editingEmployee.can_approve_reservations || false} onCheckedChange={(c) => setEditingEmployee({...editingEmployee, can_approve_reservations: c})} /></div>
            </div>

            <div className="border rounded-xl p-3 space-y-2">
              <div className="flex items-center gap-2 mb-2"><Wallet className="w-4 h-4 text-navy" /><span className="font-medium text-navy">{t('common:payments')}</span></div>
              <div className="space-y-1.5">
                <div className="flex items-center justify-between p-2 bg-slate-50 rounded-lg text-sm"><span className="text-grey">{t("packages:packagePayments")}</span><Switch checked={editingEmployee.permissions?.payments || false} onCheckedChange={(c) => setEditingEmployee({...editingEmployee, permissions: {...editingEmployee.permissions, payments: c}})} /></div>
                <div className={`flex items-center justify-between p-2 bg-slate-50 rounded-lg text-sm ${!editingEmployee.is_manager ? 'opacity-50' : ''}`}><span className="text-grey flex items-center gap-1">{t('employees:salaryPayments')} <span className="text-xs text-amber-600 bg-amber-100 px-1 rounded">{t('employees:manager')}</span></span><Switch checked={editingEmployee.permissions?.salary_payments || false} disabled={!editingEmployee.is_manager} onCheckedChange={(c) => setEditingEmployee({...editingEmployee, permissions: {...editingEmployee.permissions, salary_payments: c}})} /></div>
                <div className={`flex items-center justify-between p-2 bg-slate-50 rounded-lg text-sm ${!editingEmployee.is_manager ? 'opacity-50' : ''}`}><span className="text-grey flex items-center gap-1">{t('employees:bonusPayments')} <span className="text-xs text-amber-600 bg-amber-100 px-1 rounded">{t('employees:manager')}</span></span><Switch checked={editingEmployee.permissions?.bonus_payments || false} disabled={!editingEmployee.is_manager} onCheckedChange={(c) => setEditingEmployee({...editingEmployee, permissions: {...editingEmployee.permissions, bonus_payments: c}})} /></div>
                <div className={`flex items-center justify-between p-2 bg-slate-50 rounded-lg text-sm ${!editingEmployee.is_manager ? 'opacity-50' : ''}`}><span className="text-grey flex items-center gap-1">{t('common:businessExpenses')} <span className="text-xs text-amber-600 bg-amber-100 px-1 rounded">{t('employees:manager')}</span></span><Switch checked={editingEmployee.permissions?.business_expenses || false} disabled={!editingEmployee.is_manager} onCheckedChange={(c) => setEditingEmployee({...editingEmployee, permissions: {...editingEmployee.permissions, business_expenses: c}})} /></div>
                <div className={`flex items-center justify-between p-2 bg-slate-50 rounded-lg text-sm ${!editingEmployee.is_manager ? 'opacity-50' : ''}`}><span className="text-grey flex items-center gap-1">{t('common:incomingRentCommission')} <span className="text-xs text-amber-600 bg-amber-100 px-1 rounded">{t('employees:manager')}</span></span><Switch checked={editingEmployee.permissions?.trainer_collections || false} disabled={!editingEmployee.is_manager} onCheckedChange={(c) => setEditingEmployee({...editingEmployee, permissions: {...editingEmployee.permissions, trainer_collections: c}})} /></div>
                <div className={`flex items-center justify-between p-2 bg-slate-50 rounded-lg text-sm ${!editingEmployee.is_manager ? 'opacity-50' : ''}`}><span className="text-grey flex items-center gap-1">{t('common:commissions')} <span className="text-xs text-amber-600 bg-amber-100 px-1 rounded">{t('employees:manager')}</span></span><Switch checked={editingEmployee.permissions?.commissions || false} disabled={!editingEmployee.is_manager} onCheckedChange={(c) => setEditingEmployee({...editingEmployee, permissions: {...editingEmployee.permissions, commissions: c}})} /></div>
                <div className={`flex items-center justify-between p-2 bg-slate-50 rounded-lg text-sm ${!editingEmployee.is_manager ? 'opacity-50' : ''}`}><span className="text-grey flex items-center gap-1">{t('common:reports')} <span className="text-xs text-amber-600 bg-amber-100 px-1 rounded">{t('employees:manager')}</span></span><Switch checked={editingEmployee.permissions?.reports || false} disabled={!editingEmployee.is_manager} onCheckedChange={(c) => setEditingEmployee({...editingEmployee, permissions: {...editingEmployee.permissions, reports: c}})} /></div>
              </div>
            </div>

            <div className="border rounded-xl p-3 space-y-2 border-amber-200 bg-amber-50/30">
              <div className="flex items-center justify-between"><span className="font-medium text-navy flex items-center gap-2"><Layers className="w-4 h-4" /> {t('admin:businessSettings')} <span className="text-xs text-amber-600 bg-amber-100 px-1 rounded">{t('employees:manager')}</span></span>
              <Switch checked={editingEmployee.permissions?.settings || false} disabled={!editingEmployee.is_manager} onCheckedChange={(c) => setEditingEmployee({...editingEmployee, permissions: {...editingEmployee.permissions, settings: c, rooms: c ? editingEmployee.permissions?.rooms : false, packages: c ? editingEmployee.permissions?.packages : false, price_list: c ? editingEmployee.permissions?.price_list : false, trainers: c ? editingEmployee.permissions?.trainers : false, employees: c ? editingEmployee.permissions?.employees : false}})} /></div>
              <div className={`ml-6 space-y-1.5 ${!editingEmployee.is_manager || editingEmployee.permissions?.settings === false ? 'opacity-50' : ''}`}>
                <div className="flex items-center justify-between p-2 bg-slate-50 rounded-lg text-sm"><span className="text-grey">{t('common:rooms')}</span><Switch checked={editingEmployee.permissions?.rooms || false} disabled={!editingEmployee.is_manager || editingEmployee.permissions?.settings === false} onCheckedChange={(c) => setEditingEmployee({...editingEmployee, permissions: {...editingEmployee.permissions, rooms: c}})} /></div>
                <div className="flex items-center justify-between p-2 bg-slate-50 rounded-lg text-sm"><span className="text-grey">{t('common:packages')}</span><Switch checked={editingEmployee.permissions?.packages || false} disabled={!editingEmployee.is_manager || editingEmployee.permissions?.settings === false} onCheckedChange={(c) => setEditingEmployee({...editingEmployee, permissions: {...editingEmployee.permissions, packages: c}})} /></div>
                <div className="flex items-center justify-between p-2 bg-slate-50 rounded-lg text-sm"><span className="text-grey">{t('common:priceLists')}</span><Switch checked={editingEmployee.permissions?.price_list || false} disabled={!editingEmployee.is_manager || editingEmployee.permissions?.settings === false} onCheckedChange={(c) => setEditingEmployee({...editingEmployee, permissions: {...editingEmployee.permissions, price_list: c}})} /></div>
                <div className="flex items-center justify-between p-2 bg-slate-50 rounded-lg text-sm"><span className="text-grey">{t('common:trainers')}</span><Switch checked={editingEmployee.permissions?.trainers || false} disabled={!editingEmployee.is_manager || editingEmployee.permissions?.settings === false} onCheckedChange={(c) => setEditingEmployee({...editingEmployee, permissions: {...editingEmployee.permissions, trainers: c}})} /></div>
                <div className="flex items-center justify-between p-2 bg-slate-50 rounded-lg text-sm"><span className="text-grey">{t('employees:employees')}</span><Switch checked={editingEmployee.permissions?.employees || false} disabled={!editingEmployee.is_manager || editingEmployee.permissions?.settings === false} onCheckedChange={(c) => setEditingEmployee({...editingEmployee, permissions: {...editingEmployee.permissions, employees: c}})} /></div>
              </div>
            </div>

            {/* Ayarlar Sayfası - alt yetkilendirme (Yönetici-only) */}
            <div className="border rounded-xl p-3 space-y-2 border-amber-200 bg-amber-50/30" data-testid="settings-page-permissions-block">
              <div className="flex items-center justify-between">
                <span className="font-medium text-navy flex items-center gap-2">
                  <Layers className="w-4 h-4" /> {t("employees:settingsPagePermissions", "Ayarlar Sayfası")}
                  <span className="text-xs text-amber-600 bg-amber-100 px-1 rounded">{t("employees:manager")}</span>
                </span>
              </div>
              <p className="text-xs text-grey">{t("employees:settingsPagePermissionsHint", "Sol menüdeki Ayarlar sayfasında hangi bölümleri görebileceğini seçin.")}</p>
              <div className={`ml-2 grid grid-cols-1 sm:grid-cols-2 gap-1.5 ${!editingEmployee.is_manager ? "opacity-50" : ""}`}>
                {[
                  { k: "settings_business_info", label: t("settings:businessInfo") },
                  { k: "settings_pricing", label: t("settings:cardPricesTitle") },
                  { k: "settings_accruals", label: t("settings:accrualDaysTitle") },
                  { k: "settings_working_hours", label: t("settings:workingHoursTitle") },
                  { k: "settings_branches", label: t("settings:branchesTitle") },
                  { k: "settings_training_types", label: t("settings:trainingTypesTitle") },
                  { k: "settings_measurements", label: t("settings:measurementParamsTitle") },
                  { k: "settings_expense_types", label: t("settings:expenseTypesTitle") },
                  { k: "settings_numbering", label: t("employees:numberingSettings", "Numaralandırma") },
                  { k: "settings_currency", label: t("common:currency") },
                ].map((it) => (
                  <div key={it.k} className="flex items-center justify-between p-2 bg-slate-50 rounded-lg text-sm" data-testid={`perm-${it.k}-employee`}>
                    <span className="text-grey">{it.label}</span>
                    <Switch
                      checked={editingEmployee.permissions?.[it.k] || false}
                      disabled={!editingEmployee.is_manager}
                      onCheckedChange={(c) => setEditingEmployee({ ...editingEmployee, permissions: { ...editingEmployee.permissions, [it.k]: c } })}
                    />
                  </div>
                ))}
              </div>
            </div>

            <div className="border rounded-xl p-3 space-y-2">
              <div className="flex items-center justify-between"><div className="flex items-center gap-2"><MessageCircle className="w-4 h-4 text-navy" /><span className="font-medium text-navy">{t('business:contact')}</span></div>
              <Switch checked={editingEmployee.permissions?.communication !== false} onCheckedChange={(c) => { setEditingEmployee({...editingEmployee, permissions: {...editingEmployee.permissions, communication: c, messages: c ? editingEmployee.permissions?.messages : false, ratings: c ? editingEmployee.permissions?.ratings : false, surveys: c ? editingEmployee.permissions?.surveys : false, events: c ? editingEmployee.permissions?.events : false, evaluations: c ? editingEmployee.permissions?.evaluations : false, goals: c ? editingEmployee.permissions?.goals : false}}); }} /></div>
              <div className={`space-y-1.5 ml-2 ${!editingEmployee.permissions?.communication && editingEmployee.permissions?.communication !== undefined ? 'opacity-50' : ''}`}>
                <div className="flex items-center justify-between p-2 bg-slate-50 rounded-lg text-sm"><span className="text-grey">{t('common:messages')}</span><Switch checked={editingEmployee.permissions?.messages !== false} disabled={editingEmployee.permissions?.communication === false} onCheckedChange={(c) => setEditingEmployee({...editingEmployee, permissions: {...editingEmployee.permissions, messages: c}})} /></div>
                <div className="flex items-center justify-between p-2 bg-slate-50 rounded-lg text-sm"><span className="text-grey">{t('business:evaluations')}</span><Switch checked={editingEmployee.permissions?.ratings || false} disabled={editingEmployee.permissions?.communication === false} onCheckedChange={(c) => setEditingEmployee({...editingEmployee, permissions: {...editingEmployee.permissions, ratings: c}})} /></div>
                <div className="flex items-center justify-between p-2 bg-slate-50 rounded-lg text-sm"><span className="text-grey">{t('common:surveys')}</span><Switch checked={editingEmployee.permissions?.surveys || false} disabled={editingEmployee.permissions?.communication === false} onCheckedChange={(c) => setEditingEmployee({...editingEmployee, permissions: {...editingEmployee.permissions, surveys: c}})} /></div>
                <div className="flex items-center justify-between p-2 bg-slate-50 rounded-lg text-sm"><span className="text-grey">{t('common:events')}</span><Switch checked={editingEmployee.permissions?.events || false} disabled={editingEmployee.permissions?.communication === false} onCheckedChange={(c) => setEditingEmployee({...editingEmployee, permissions: {...editingEmployee.permissions, events: c}})} /></div>
                <div className="flex items-center justify-between p-2 bg-slate-50 rounded-lg text-sm"><span className="text-grey">{t("evaluations:memberEvaluations")}</span><Switch checked={editingEmployee.permissions?.evaluations || false} disabled={editingEmployee.permissions?.communication === false} onCheckedChange={(c) => setEditingEmployee({...editingEmployee, permissions: {...editingEmployee.permissions, evaluations: c}})} /></div>
                <div className="flex items-center justify-between p-2 bg-slate-50 rounded-lg text-sm"><span className="text-grey">{t('common:goalsAndBadges')}</span><Switch checked={editingEmployee.permissions?.goals || false} disabled={editingEmployee.permissions?.communication === false} onCheckedChange={(c) => setEditingEmployee({...editingEmployee, permissions: {...editingEmployee.permissions, goals: c}})} /></div>
              </div>
            </div>

            <div className="border rounded-xl p-3">
              <div className="flex items-center justify-between"><div className="flex items-center gap-2"><TrendingUp className="w-4 h-4 text-navy" /><span className="font-medium text-navy">{t('common:viewRevenueInfo')}</span><span className="text-xs text-blue-600 bg-blue-100 px-1 rounded">{t("common:private")}</span></div><Switch checked={editingEmployee.permissions?.can_view_revenue || false} onCheckedChange={(c) => setEditingEmployee({...editingEmployee, permissions: {...editingEmployee.permissions, can_view_revenue: c}})} /></div>
              <p className="text-xs text-grey mt-2">{t("reports:buIzinAciksaGenelBakistaki")}</p>
            </div>

            <div className="border rounded-xl p-3">
              <div className="flex items-center justify-between"><div className="flex items-center gap-2"><TrendingUp className="w-4 h-4 text-navy" /><span className="font-medium text-navy">{t('members:deactivateMemberPermission', 'Üyeyi Pasifleştirme')}</span></div><Switch data-testid="employee-perm-deactivate-members" checked={editingEmployee.permissions?.can_deactivate_members || false} onCheckedChange={(c) => setEditingEmployee({...editingEmployee, permissions: {...editingEmployee.permissions, can_deactivate_members: c}})} /></div>
              <p className="text-xs text-grey mt-2">{t("members:deactivateMemberPermissionHint", "Açıksa üyeleri pasif/aktif yapabilir.")}</p>
            </div>

            <div className={`border rounded-xl p-3 ${!editingEmployee.is_manager ? 'opacity-50' : ''}`}>
              <div className="flex items-center justify-between"><div className="flex items-center gap-2"><TrendingUp className="w-4 h-4 text-navy" /><span className="font-medium text-navy">Devir Paketi Girişi</span><span className="text-xs text-amber-600 bg-amber-100 px-1 rounded">{t('employees:manager')}</span></div><Switch data-testid="employee-perm-transfer-package" checked={editingEmployee.is_manager && editingEmployee.permissions?.transfer_package_sale !== false} disabled={!editingEmployee.is_manager} onCheckedChange={(c) => setEditingEmployee({...editingEmployee, permissions: {...editingEmployee.permissions, transfer_package_sale: c}})} /></div>
              <p className="text-xs text-grey mt-2">Açıksa sistem öncesi satılmış paketleri (kalan dersleriyle) girebilir.</p>
            </div>

            {editingEmployee.is_manager && (
              <div className="border rounded-xl p-3 border-amber-200 bg-amber-50/50">
                <div className="flex items-center justify-between"><div className="flex items-center gap-2"><Check className="w-4 h-4 text-amber-600" /><span className="font-medium text-navy">{t('common:manualReservationComplete')}</span><span className="text-xs text-amber-600 bg-amber-100 px-1 rounded">{t('employees:manager')}</span></div><Switch checked={editingEmployee.permissions?.manual_reservation_complete || false} onCheckedChange={(c) => setEditingEmployee({...editingEmployee, permissions: {...editingEmployee.permissions, manual_reservation_complete: c}})} /></div>
                <p className="text-xs text-grey mt-2">{t("employees:buIzinAciksaDersSaatinden")}</p>
              </div>
            )}

            {editingEmployee.is_manager && (
              <div className="border rounded-xl p-3 border-amber-200 bg-amber-50/50">
                <div className="flex items-center justify-between"><div className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-amber-600" /><span className="font-medium text-navy">{t('common:manualReservationApprove')}</span><span className="text-xs text-amber-600 bg-amber-100 px-1 rounded">{t('employees:manager')}</span></div><Switch checked={editingEmployee.permissions?.manual_reservation_approve || false} onCheckedChange={(c) => setEditingEmployee({...editingEmployee, permissions: {...editingEmployee.permissions, manual_reservation_approve: c}})} /></div>
                <p className="text-xs text-grey mt-2">{t("employees:skipApprovalHint")}</p>
              </div>
            )}
            
            {/* Geçmiş Tarihli Rezervasyon */}
            {editingEmployee.is_manager && (
              <div className="border rounded-xl p-3 border-amber-200 bg-amber-50/50" data-testid="perm-backdate_reservations-employee-block">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Calendar className="w-4 h-4 text-amber-600" />
                    <span className="font-medium text-navy">{t("employees:backdateReservations", "Geçmiş Tarihli Rezervasyon")}</span>
                    <span className="text-xs text-amber-600 bg-amber-100 px-1 rounded">{t('employees:manager')}</span>
                  </div>
                  <Switch
                    checked={editingEmployee.permissions?.backdate_reservations !== false}
                    onCheckedChange={(c) => setEditingEmployee({...editingEmployee, permissions: {...editingEmployee.permissions, backdate_reservations: c}})}
                    data-testid="perm-backdate_reservations-employee"
                  />
                </div>
                <p className="text-xs text-grey mt-2">Açıksa: Yönetici, "Min. Önceden Rezervasyon" süresine bakmaksızın geçmiş tarihli rezervasyon oluşturabilir.</p>
              </div>
            )}
            
            {/* Ders Açıklaması Düzenleme */}
            {editingEmployee.is_manager && (
              <div className="border rounded-xl p-3 border-amber-200 bg-amber-50/50" data-testid="perm-lesson_note_edit-employee-block">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <FileText className="w-4 h-4 text-amber-600" />
                    <span className="font-medium text-navy">{t("employees:lessonNoteEdit", "Ders Açıklaması Düzenleme")}</span>
                    <span className="text-xs text-amber-600 bg-amber-100 px-1 rounded">{t('employees:manager')}</span>
                  </div>
                  <Switch
                    checked={editingEmployee.permissions?.lesson_note_edit !== false}
                    onCheckedChange={(c) => setEditingEmployee({...editingEmployee, permissions: {...editingEmployee.permissions, lesson_note_edit: c}})}
                    data-testid="perm-lesson_note_edit-employee"
                  />
                </div>
                <p className="text-xs text-grey mt-2">Açıksa: Tamamlanan derslere açıklama (not) ekleyebilir ve sonradan düzenleyebilir.</p>
              </div>
            )}
            
            {/* Üye No / Paket No Düzenleme İzinleri */}
            {editingEmployee.is_manager && (
              <div className="border rounded-xl p-3 border-amber-200 bg-amber-50/50" data-testid="perm-member-package-no-edit-employee-block">
                <div className="flex items-center gap-2 mb-3">
                  <Edit className="w-4 h-4 text-amber-600" />
                  <span className="font-medium text-navy">{t("employees:numberEditPermissions", "Numara Düzenleme")}</span>
                  <span className="text-xs text-amber-600 bg-amber-100 px-1 rounded">{t('employees:manager')}</span>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  <label className="flex items-center justify-between bg-white rounded-lg p-2 border border-slate-200">
                    <span className="text-sm">{t("employees:memberNoEdit", "Üye No Düzenleme")}</span>
                    <Switch
                      checked={editingEmployee.permissions?.member_no_edit !== false}
                      onCheckedChange={(c) => setEditingEmployee({...editingEmployee, permissions: {...editingEmployee.permissions, member_no_edit: c}})}
                      data-testid="perm-member_no_edit-employee"
                    />
                  </label>
                  <label className="flex items-center justify-between bg-white rounded-lg p-2 border border-slate-200">
                    <span className="text-sm">{t("employees:packageNoEdit", "Paket No Düzenleme")}</span>
                    <Switch
                      checked={editingEmployee.permissions?.package_no_edit !== false}
                      onCheckedChange={(c) => setEditingEmployee({...editingEmployee, permissions: {...editingEmployee.permissions, package_no_edit: c}})}
                      data-testid="perm-package_no_edit-employee"
                    />
                  </label>
                </div>
              </div>
            )}
          </TabsContent>
        </Tabs>

        <DialogFooter className="gap-2 mt-4">
          <Button variant="outline" className="rounded-xl" onClick={onClose}>{t('common:cancel')}</Button>
          <Button className="bg-navy hover:bg-navy/90 rounded-xl" onClick={handleSave} disabled={isSaving}>
            {isSaving ? t('common:saving') : t('common:save')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
